import { useTranslation } from '@egw/i18n'
import { loadVGSCollect } from '@vgs/collect-js'
import { useRef, useState } from 'react'
import { Cookies } from '@egw/clients'
import { useAuth } from '@egw/auth'
import { useFlags } from '@egw/feature-flags'
import { type FeatureFlags } from '@egw/constants'
import { Logger } from '@egw/logger'
import { configClient } from '@egw/config'
import { sendEvent } from '@egw/events'
import { type Config } from '../types'

const log = new Logger('VGS Collect')

export const pin_input_styles = {
  color: 'black',
  'line-height': '1.5rem',
  'font-size': '1rem',
  height: '1rem',
  width: '93%',
  border: '1px solid rgb(212 212 216)', // zinc-300
  padding: '1.25rem 3% 1.25rem 3%',
  'border-radius': '4px',
  margin: '0 auto',
}

declare global {
  interface Window {
    VGSCollect: any
  }
}

interface ISubmitFormParams {
  cardId?: string
  pin?: string
}

const VGS_PIN_PATH = '/api/secure/card/pin'
const VGS_COLLECT_VERSION = '2.18.0'
const PIN_VALIDATION_REGEX = '/^[0-9]{4}$/'

export enum EVGSFormStatus {
  INITIAL = 'INITIAL',
  FAILURE = 'FAILURE',
  SUCCESS = 'SUCCESS',
  LOADING = 'LOADING',
  RETRY = 'RETRY',
}

export default function useVGSCollect(pinFieldTarget: string) {
  // -- State ------------------------------------------

  const config = configClient.getConfig() as Config

  const [formStatus, setFormStatus] = useState<EVGSFormStatus>(EVGSFormStatus.INITIAL)
  const [errorMessage, setErrorMessage] = useState<string>()

  const { accessToken } = useAuth()
  const { flags } = useFlags<FeatureFlags>()

  // -- Hooks ------------------------------------------
  const { t } = useTranslation('features-account')

  // -- Refs ------------------------------------------
  const vgsForm = useRef<any>()

  // -- Handlers --------------------------------------
  const makeForm = async () => {
    log.info('make form invoked')
    // Create VGS Collect API object
    const vgs_collect = await loadVGSCollect({
      vaultId: config.vgs.vgsVaultId,
      environment: config.vgs.vgsEnv,
      version: VGS_COLLECT_VERSION,
    }).catch((e) => {
      console.log(e)
    })

    log.info('vgs collect', { vgs_collect })

    // Init form with fields
    const initForm = async (vgs_collect: any) => {
      log.info('Initializing VGS Form')
      // TODO: find a nicer way of making the makeForm call idempotent
      if (document.getElementById(pinFieldTarget)?.innerHTML) {
        document.getElementById(pinFieldTarget)!.innerHTML = ''
      }

      const form = vgs_collect.init()
      form.useCname(config.vgs.vgsCName)

      const pinFieldConfig = {
        type: 'text',
        name: 'pin',
        css: pin_input_styles,
        placeholder: t('debit_card.set_4_digit_pin'),
        validations: ['required', PIN_VALIDATION_REGEX],
        inputMode: 'numeric',
        maxLength: 4,
        hideValue: true,
        classes: {
          invalid: 'text-error',
        },
      }

      form.field(`#${pinFieldTarget}`, pinFieldConfig)
      return form
    }

    // store form in ref so it can be submitted asynchronously
    vgsForm.current = await initForm(vgs_collect)
  }

  function submitForm(data: ISubmitFormParams, onSuccess?: () => void) {
    setFormStatus(EVGSFormStatus.LOADING)
    setErrorMessage('')
    try {
      ;(vgsForm.current.submit(
        VGS_PIN_PATH,
        {
          data,
          headers: {
            'p-session-id': Cookies.get('p-session-id') as string,
            Authorization: `Bearer ${accessToken}`,
          },
        },
        (response: unknown) => {
          log.info('VGS Collect form response: ', response)
          // Submit response callback
          if (response === 200) {
            log.info('VGS Collect form response success')
            sendEvent('eg_setPIN_success', {
              eventSource: 'egWealth',
              category: 'card-activation-pin',
              subCategory: 'result',
            })
            setFormStatus(EVGSFormStatus.SUCCESS)
            onSuccess && onSuccess()
          } else {
            log.error('error submitting VGS Collect form', response)
            sendEvent('eg_setPIN_fail', {
              eventSource: 'egWealth',
              category: 'card-activation-pin',
              subCategory: 'result',
            })
            setFormStatus(EVGSFormStatus.FAILURE)
          }
        },
        (response: any) => {
          const hasError = response.pin.errors?.length > 0
          if (hasError) {
            setFormStatus(EVGSFormStatus.RETRY)
            setErrorMessage(response.pin.errors?.[0].message)
            sendEvent('eg_setPIN_fail', {
              eventSource: 'egWealth',
              category: 'card-activation-pin',
              subCategory: 'result',
            })
          }
        },
      ),
        (response: any) => {
          // Submit validation callback
          log.info(response)
        })
    } catch (err) {
      // TODO this does not catch VGS errors / valdation errors
      setFormStatus(EVGSFormStatus.FAILURE)
      log.error('submitting the VGS collect form failed', err)
      sendEvent('eg_setPIN_fail', {
        eventSource: 'egWealth',
        category: 'card-activation-pin',
        subCategory: 'result',
      })
      return false
    }
  }

  return {
    makeForm,
    submitForm,
    formStatus,
    setFormStatus,
    getErrorMessage: () => errorMessage,
  }
}
